"use client";

import { memo, useCallback, useEffect, useMemo, useRef, useState } from "react";
import Logo from "@/app/components/Logo";
import Notice from "@/app/components/Notice";
import Button from "@/app/components/Button";
import TextField from "@/app/components/TextField";
import TextAreaField from "@/app/components/TextAreaField";
import SelectField from "@/app/components/SelectField";
import { useConfirm } from "@/app/components/ConfirmDialog";
import { useGateHold } from "@/app/components/Gate";
import { AccountView } from "@/lib/types";

const WORLD = "Baunheim";

type Mode = "survey" | "text" | "full";
type Step = "method" | "input" | "done";
type Answer = number | string;

interface Question {
  id: string;
  type: "scale5" | "choice";
  text: string;
  options?: string[];
}

interface SheetField {
  id: string;
  label: string;
  kind: "text" | "textarea" | "select" | "select-free";
  max?: number;
  note?: string;
  options?: string;
}

interface FormDef {
  survey: { scale5: string[]; questions: Question[]; request: { id: string; text: string } };
  sheet: SheetField[];
  options: Record<string, string[]>;
  limits: { request: number };
}

interface Draft {
  mode: Mode | null;
  answers: Record<string, Answer>;
  request: string;
  sheet: Record<string, string>;
  /** 同行者の依頼文。3 人まで */
  companions: string[];
}

/** 1 人の PC が持てる同行者の数 */
const COMPANION_MAX = 3;

const EMPTY_DRAFT: Draft = { mode: null, answers: {}, request: "", sheet: {}, companions: [] };

/** 書きかけをブラウザへ覚えるまでの間。打ち止めてからこの間が過ぎたら覚える */
const SAVE_MS = 800;

const NO_OPTIONS: string[] = [];

const METHODS: { mode: Mode; title: string; lines: string[] }[] = [
  {
    mode: "survey",
    title: "アンケートに答える",
    lines: ["19 の問いに答えて作ります。世界を知らなくても答えられます。", "要望があれば最後に書き添えられます。"],
  },
  {
    mode: "text",
    title: "文章で依頼する",
    lines: ["どんなキャラクターにしたいかを文章で書きます。", "書かれていない部分は、文章に沿って作り手が決めます。"],
  },
  {
    mode: "full",
    title: "詳細に指定する",
    lines: ["名前・経歴・外見・口調などの章立てを自分で書きます。", "空けた欄は、作り手が決めます。"],
  },
];

function draftKey(accountId: string, region: string): string {
  return `rakuen-create-draft:${accountId}:${WORLD}:${region}`;
}

function loadDraft(key: string): Draft {
  try {
    const text = window.localStorage.getItem(key);
    if (!text) return EMPTY_DRAFT;
    const body = JSON.parse(text) as Partial<Draft>;
    return {
      mode: body.mode === "survey" || body.mode === "text" || body.mode === "full" ? body.mode : null,
      answers: body.answers && typeof body.answers === "object" ? body.answers : {},
      request: typeof body.request === "string" ? body.request : "",
      sheet: body.sheet && typeof body.sheet === "object" ? body.sheet : {},
      companions: Array.isArray(body.companions) ? body.companions.filter((one): one is string => typeof one === "string").slice(0, COMPANION_MAX) : [],
    };
  } catch {
    return EMPTY_DRAFT;
  }
}

/** 5 段の問いの答えの欄 */
function ScaleAnswer({
  labels,
  value,
  onChange,
  disabled,
}: {
  labels: string[];
  value: Answer | undefined;
  onChange: (value: number) => void;
  disabled: boolean;
}) {
  return (
    <div>
      <div className="scale" role="radiogroup">
        {labels.map((label, index) => (
          <button
            key={label}
            type="button"
            role="radio"
            aria-checked={value === index + 1}
            aria-label={label}
            title={label}
            className={value === index + 1 ? "scale-dot scale-dot-on" : "scale-dot"}
            disabled={disabled}
            onClick={() => onChange(index + 1)}
          />
        ))}
      </div>
      <div className="scale-ends">
        <span>{labels[0]}</span>
        <span>{labels[labels.length - 1]}</span>
      </div>
    </div>
  );
}

/** 選ぶ問いの答えの欄 */
function ChoiceAnswer({
  options,
  value,
  onChange,
  disabled,
}: {
  options: string[];
  value: Answer | undefined;
  onChange: (value: string) => void;
  disabled: boolean;
}) {
  return (
    <div className="choices" role="radiogroup">
      {options.map((option) => (
        <button
          key={option}
          type="button"
          role="radio"
          aria-checked={value === option}
          className={value === option ? "choice choice-on" : "choice"}
          disabled={disabled}
          onClick={() => onChange(option)}
        >
          {option}
        </button>
      ))}
    </div>
  );
}

const FREE_INPUT = "自由入力";

/** 候補から選ぶか、自由に書く欄 */
function SelectFreeField({
  field,
  options,
  value,
  onChange,
  disabled,
}: {
  field: SheetField;
  options: string[];
  value: string;
  onChange: (value: string) => void;
  disabled: boolean;
}) {
  const [free, setFree] = useState<boolean | null>(null);
  const isFree = free ?? (value !== "" && !options.includes(value));

  return (
    <>
      <SelectField
        id={`sheet-${field.id}`}
        label={field.label}
        value={isFree ? FREE_INPUT : value}
        onChange={(next) => {
          setFree(next === FREE_INPUT);
          onChange(next === FREE_INPUT ? "" : next);
        }}
        options={[...options, FREE_INPUT]}
        placeholder="おまかせ"
        note={field.note}
        disabled={disabled}
      />
      {isFree ? (
        <TextField
          id={`sheet-${field.id}-free`}
          label={`${field.label}（自由入力）`}
          value={value}
          onChange={(next) => onChange(field.max ? next.slice(0, field.max) : next)}
          disabled={disabled}
        />
      ) : null}
    </>
  );
}

/** 章立ての 1 欄。打ったときに組み直すのは、替わった欄だけにする */
const SheetInput = memo(function SheetInput({
  field,
  options,
  value,
  onChange,
  disabled,
}: {
  field: SheetField;
  options: string[];
  value: string;
  onChange: (id: string, value: string) => void;
  disabled: boolean;
}) {
  const change = (next: string) => onChange(field.id, next);
  return field.kind === "select-free" ? (
    <SelectFreeField field={field} options={options} value={value} onChange={change} disabled={disabled} />
  ) : field.kind === "select" ? (
    <SelectField
      id={`sheet-${field.id}`}
      label={field.label}
      value={value}
      onChange={change}
      options={options}
      placeholder="おまかせ"
      note={field.note}
      disabled={disabled}
    />
  ) : field.kind === "textarea" ? (
    <TextAreaField
      id={`sheet-${field.id}`}
      label={field.label}
      value={value}
      onChange={change}
      note={field.note}
      rows={field.id === "history" ? 8 : 3}
      max={field.max}
      disabled={disabled}
    />
  ) : (
    <TextField
      id={`sheet-${field.id}`}
      label={field.label}
      value={value}
      onChange={(next) => change(field.max ? next.slice(0, field.max) : next)}
      note={field.note}
      disabled={disabled}
    />
  );
});

/** アンケートの 1 問。答えたときに組み直すのは、替わった問いだけにする */
const QuestionItem = memo(function QuestionItem({
  question,
  index,
  labels,
  value,
  onChange,
  disabled,
}: {
  question: Question;
  index: number;
  labels: string[];
  value: Answer | undefined;
  onChange: (id: string, value: Answer) => void;
  disabled: boolean;
}) {
  return (
    <div className="question">
      <p className="question-text">
        <span className="question-number">Q{index + 1}</span>
        {question.text}
      </p>
      {question.type === "scale5" ? (
        <ScaleAnswer labels={labels} value={value} onChange={(next) => onChange(question.id, next)} disabled={disabled} />
      ) : (
        <ChoiceAnswer
          options={question.options ?? NO_OPTIONS}
          value={value}
          onChange={(next) => onChange(question.id, next)}
          disabled={disabled}
        />
      )}
    </div>
  );
});

/** 打ったときに、替わった欄だけを組み直す複数行の入力欄 */
const RequestInput = memo(TextAreaField);

/** 同行者の 1 欄。打ったときに組み直すのは、替わった欄だけにする */
const CompanionInput = memo(function CompanionInput({
  index,
  text,
  max,
  onChange,
  onRemove,
  disabled,
}: {
  index: number;
  text: string;
  max: number | undefined;
  onChange: (index: number, value: string) => void;
  onRemove: (index: number) => void;
  disabled: boolean;
}) {
  return (
    <div>
      <TextAreaField
        id={`create-companion-${index}`}
        label={`同行者 ${index + 1}`}
        value={text}
        onChange={(value) => onChange(index, value)}
        rows={5}
        max={max}
        disabled={disabled}
      />
      <Button onClick={() => onRemove(index)} disabled={disabled}>
        外す
      </Button>
    </div>
  );
});

/**
 * Baunheim のキャラクター作成の依頼を受け付ける画面。
 * npc が真のときは、管理者用画面から master の NPC の作成を依頼する形になる
 */
export default function CharacterCreate({
  region,
  account,
  requestId,
  onBack,
  onDone,
  npc = false,
}: {
  region: string;
  account: AccountView;
  requestId: string | null;
  onBack: () => void;
  onDone: (account?: AccountView) => void;
  npc?: boolean;
}) {
  const key = draftKey(account.id, npc ? "npc" : region);
  const formUrl = npc ? `/api/admin/npc/form?world=${WORLD}` : `/api/character/form?world=${WORLD}&region=${encodeURIComponent(region)}`;
  const [form, setForm] = useState<FormDef | null>(null);
  const [step, setStep] = useState<Step>("method");
  const [draft, setDraft] = useState<Draft>(EMPTY_DRAFT);
  const [draftLoaded, setDraftLoaded] = useState(false);
  const [message, setMessage] = useState("");
  const [sending, setSending] = useState(false);
  const [acceptedId, setAcceptedId] = useState("");
  const [nextAccount, setNextAccount] = useState<AccountView | undefined>(undefined);
  const [ask, confirmDialog] = useConfirm();
  // 入力フォームと書きかけを読み終えるまで、ロード画面に待ってもらう
  useGateHold((form === null || !draftLoaded) && message === "");

  useEffect(() => {
    let alive = true;
    fetch(formUrl)
      .then(async (response) => {
        const body = await response.json();
        if (!alive) return;
        if (response.ok && !body.error) {
          setForm(body as FormDef);
        } else {
          setMessage(body.error ?? "入力フォームを読めません");
        }
      })
      .catch(() => {
        if (alive) setMessage("サーバーに繋がりません");
      });
    return () => {
      alive = false;
    };
  }, [formUrl]);

  // 新しい依頼の書きかけはブラウザに覚える。やり直しは受け付けた依頼の中身から始める
  useEffect(() => {
    if (requestId === null) {
      setDraft(loadDraft(key));
      setDraftLoaded(true);
      return;
    }

    let alive = true;
    fetch(`/api/character/request?world=${WORLD}&region=${encodeURIComponent(region)}&id=${encodeURIComponent(requestId)}`)
      .then(async (response) => {
        const body = await response.json();
        if (!alive) return;
        if (response.ok && body.request) {
          setDraft({
            mode: body.request.mode,
            answers: body.request.answers ?? {},
            request: body.request.request ?? "",
            sheet: body.request.sheet ?? {},
            companions: Array.isArray(body.request.companions) ? body.request.companions.map((one: { request?: string }) => one.request ?? "") : [],
          });
          setStep("input");
          setDraftLoaded(true);
        } else {
          setMessage(body.error ?? "依頼を読めません");
        }
      })
      .catch(() => {
        if (alive) setMessage("サーバーに繋がりません");
      });
    return () => {
      alive = false;
    };
  }, [key, region, requestId]);

  // 書きかけは、打ち止めて SAVE_MS が過ぎたら覚える。画面を離れるときと裏へ回るときは、待たずに覚える
  const unsaved = useRef<Draft | null>(null);
  const flush = useCallback(() => {
    if (unsaved.current === null) return;
    try {
      window.localStorage.setItem(key, JSON.stringify(unsaved.current));
    } catch {
      // 覚えられない端末では書きかけを持たない
    }
    unsaved.current = null;
  }, [key]);

  useEffect(() => {
    if (!draftLoaded || requestId !== null) return;
    unsaved.current = draft;
    const timer = setTimeout(flush, SAVE_MS);
    return () => clearTimeout(timer);
  }, [draft, draftLoaded, requestId, flush]);

  useEffect(() => {
    const hide = () => {
      if (document.visibilityState === "hidden") flush();
    };
    document.addEventListener("visibilitychange", hide);
    window.addEventListener("pagehide", flush);
    return () => {
      document.removeEventListener("visibilitychange", hide);
      window.removeEventListener("pagehide", flush);
      flush();
    };
  }, [flush]);

  const setAnswer = useCallback((id: string, value: Answer) => {
    setDraft((current) => ({ ...current, answers: { ...current.answers, [id]: value } }));
  }, []);

  const setSheet = useCallback((id: string, value: string) => {
    setDraft((current) => ({ ...current, sheet: { ...current.sheet, [id]: value } }));
  }, []);

  const setRequest = useCallback((value: string) => {
    setDraft((current) => ({ ...current, request: value }));
  }, []);

  const setCompanion = useCallback((index: number, value: string) => {
    setDraft((current) => ({ ...current, companions: current.companions.map((one, j) => (j === index ? value : one)) }));
  }, []);

  const removeCompanion = useCallback((index: number) => {
    setDraft((current) => ({ ...current, companions: current.companions.filter((_, j) => j !== index) }));
  }, []);

  const mode = draft.mode;
  const questions = form?.survey.questions ?? [];
  const unanswered = useMemo(
    () => questions.filter((question) => draft.answers[question.id] === undefined).map((question) => question.id.toUpperCase()),
    [questions, draft.answers]
  );
  const sheetFilled = Object.values(draft.sheet).some((value) => value.trim() !== "");

  let blocker = "";
  if (mode === "text" && draft.request.trim() === "") blocker = "依頼文を書いてください";
  if (mode === "survey" && unanswered.length > 0) blocker = `答えていない問い：${unanswered.join("・")}`;
  if (mode === "full" && !sheetFilled) blocker = "章立てを 1 つ以上書いてください";

  const send = async () => {
    if (mode === null || blocker !== "" || sending) return;
    if (!(await ask(npc ? "この内容で NPC の作成を依頼します。" : requestId === null ? "この内容でキャラクターの作成を依頼します。" : "この内容で依頼をやり直します。"))) return;

    setSending(true);
    setMessage("");
    try {
      const response = await fetch(npc ? "/api/admin/npc/request" : "/api/character/request", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          world: WORLD,
          region,
          request: {
            id: requestId,
            mode,
            answers: mode === "survey" ? draft.answers : null,
            request: draft.request,
            sheet: mode === "full" ? draft.sheet : null,
            companions: npc ? [] : draft.companions.filter((text) => text.trim() !== "").map((text) => ({ request: text })),
          },
        }),
      });
      const body = await response.json();
      if (response.ok && body.ok) {
        setAcceptedId(String(body.id));
        setNextAccount(body.account as AccountView | undefined);
        setDraft(EMPTY_DRAFT);
        setStep("done");
      } else {
        setMessage(body.error ?? "依頼を送れません");
      }
    } catch {
      setMessage("サーバーに繋がりません");
    } finally {
      setSending(false);
    }
  };

  if (step === "done") {
    return (
      <div className="panel panel-wide panel-scene panel-create">
        <Logo />
        <Notice>
          {npc ? (
            <>
              <p>NPC の作成を受け付けました。</p>
              <p>NPC の一覧に 依頼中 として並びます。ハキリリが作り終えると 確認待ち になり、確かめて決定すると master へ入ります。</p>
            </>
          ) : (
            <>
              <p>{requestId === null ? "キャラクターの作成を受け付けました。" : "依頼のやり直しを受け付けました。"}</p>
              <p>キャラクター選択画面の枠に 依頼中 として並びます。作成が始まるまでは、やり直しから内容を直せます。</p>
            </>
          )}
        </Notice>
        <p className="receipt">
          受付番号
          <strong>{acceptedId}</strong>
        </p>
        <div className="foot">
          <Button variant="primary" onClick={() => onDone(nextAccount)}>
            {npc ? "NPC の一覧へ戻る" : "キャラクター選択へ戻る"}
          </Button>
        </div>
      </div>
    );
  }

  if (step === "method" || mode === null) {
    return (
      <div className="panel panel-wide panel-scene panel-create">
        <Logo />
        <div className="head">
          <span className="head-user">
            キャラクター作成
            <strong>
              {WORLD} ／ {region}
            </strong>
          </span>
        </div>

        <div className="menu menu-flat">
          {METHODS.map((method) => (
            <button
              key={method.mode}
              type="button"
              className={draft.mode === method.mode ? "method method-on" : "method"}
              disabled={form === null || (requestId !== null && !draftLoaded)}
              onClick={() => {
                setDraft((current) => ({ ...current, mode: method.mode }));
                setMessage("");
                setStep("input");
              }}
            >
              <span className="method-title">{method.title}</span>
              {method.lines.map((line) => (
                <span className="method-line" key={line}>
                  {line}
                </span>
              ))}
            </button>
          ))}
        </div>

        <div className="foot">
          <Button onClick={onBack} inline>
            戻る
          </Button>
        </div>

        <p className="message message-info">{form === null && message === "" ? "読み込み中・・・" : message}</p>
      </div>
    );
  }

  const method = METHODS.find((entry) => entry.mode === mode);
  const survey = form?.survey;

  return (
    <div className="panel panel-wide panel-scene panel-create">
      <Logo />
      <div className="head">
        <span className="head-user">
          {WORLD} ／ {region}
          <strong>{method?.title}</strong>
        </span>
        {requestId === null ? (
          <Button onClick={() => setStep("method")} inline disabled={sending}>
            作り方を選び直す
          </Button>
        ) : null}
      </div>

      {mode === "full" && form ? (
        <section className="section">
          <h2 className="section-title">章立て</h2>
          <p className="section-note">書いた欄は一字も変えずに使います。空けた欄は作り手が決めます。</p>
          {form.sheet.map((field) => (
            <SheetInput
              key={field.id}
              field={field}
              options={form.options[field.options ?? ""] ?? NO_OPTIONS}
              value={draft.sheet[field.id] ?? ""}
              onChange={setSheet}
              disabled={sending}
            />
          ))}
        </section>
      ) : null}

      {mode === "survey" && survey ? (
        <section className="section">
          <h2 className="section-title">
            アンケート
            <span className="section-count">
              {questions.length - unanswered.length} / {questions.length}
            </span>
          </h2>
          <p className="section-note">キャラクター本人になったつもりで答えてください。</p>
          {questions.map((question, index) => (
            <QuestionItem
              key={question.id}
              question={question}
              index={index}
              labels={survey.scale5}
              value={draft.answers[question.id]}
              onChange={setAnswer}
              disabled={sending}
            />
          ))}
        </section>
      ) : null}

      <section className="section">
        <h2 className="section-title">{mode === "text" ? "依頼文" : "要望"}</h2>
        <RequestInput
          note={`${mode === "text" ? "生い立ち・性格・望み・見た目など、決めたいことを自由に書けます。" : "空のままでも依頼できます。"}${npc ? "" : "一緒に旅をする仲間は、下の「同行者」に書いてください。"}`}
          noteAbove
          id="create-request"
          label={mode === "text" ? "どんなキャラクターにしたいかを書いてください" : survey?.request.text ?? ""}
          value={draft.request}
          onChange={setRequest}
          rows={mode === "text" ? 12 : 5}
          max={form?.limits.request}
          disabled={sending}
        />
      </section>

      {npc ? null : (
        <section className="section">
          <h2 className="section-title">同行者</h2>
          <p className="section-note">一緒に旅をして戦闘にも加わる仲間は、ここで頼みます。{COMPANION_MAX} 人まで、1 人につき 1 欄に書いてください。キャラクターとの間柄・生い立ち・性格・得意なことなどを書けます。</p>
          {draft.companions.map((text, i) => (
            <CompanionInput
              key={i}
              index={i}
              text={text}
              max={form?.limits.request}
              onChange={setCompanion}
              onRemove={removeCompanion}
              disabled={sending}
            />
          ))}
          {draft.companions.length < COMPANION_MAX ? (
            <Button onClick={() => setDraft((current) => ({ ...current, companions: [...current.companions, ""] }))} disabled={sending}>
              同行者を足す
            </Button>
          ) : null}
        </section>
      )}

      <div className="actions">
        <Button variant="primary" onClick={send} disabled={blocker !== "" || sending || form === null || !draftLoaded}>
          {sending ? "送っています" : "決定"}
        </Button>
        <Button onClick={requestId === null ? () => setStep("method") : onBack} disabled={sending}>
          戻る
        </Button>
      </div>

      <p className={message ? "message" : "message message-info"}>{message || blocker}</p>
      {confirmDialog}
    </div>
  );
}
